/** Wall-clock instant in America/Toronto. October 2026 is still on daylight time. */
export function dateInToronto(iso, hour, minute) {
  const [year, month, day] = String(iso).split("-").map(Number);
  const guess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Toronto",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(guess);
  const bag = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const hourPart = bag.hour === "24" ? "0" : bag.hour;
  const asUtc = Date.UTC(Number(bag.year), Number(bag.month) - 1, Number(bag.day), Number(hourPart), Number(bag.minute), Number(bag.second));
  const offsetMs = asUtc - guess.getTime();
  return new Date(guess.getTime() - offsetMs);
}

export function torontoIso(date) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Toronto",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function torontoMinutes(date) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Toronto",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const hour = Number(parts.find((part) => part.type === "hour")?.value || 0);
  const minute = Number(parts.find((part) => part.type === "minute")?.value || 0);
  return hour * 60 + minute;
}

export function driveArrival(seconds) {
  const minutes = Math.max(0, Math.round(Number(seconds) / 60));
  const arrival = new Date(dateInToronto("2026-10-02", 6, 0).getTime() + minutes * 60000);
  const rdv = dateInToronto("2026-10-02", 13, 45);
  const marginMin = Math.round((rdv.getTime() - arrival.getTime()) / 60000);
  const abs = Math.abs(marginMin);
  const hours = Math.floor(abs / 60);
  const mins = abs % 60;
  const span = hours ? `${hours} h ${String(mins).padStart(2, "0")}` : `${mins} min`;
  return {
    arrival: clockFace(arrival),
    margin: marginMin < 0 ? `dépassée de ${span}` : span,
  };
}

export function clockFace(date) {
  const parts = new Intl.DateTimeFormat("fr-CA", {
    timeZone: "America/Toronto",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const hour = parts.find((part) => part.type === "hour")?.value || "00";
  const minute = parts.find((part) => part.type === "minute")?.value || "00";
  return `${hour} h ${minute}`;
}

export function dayPhrase(date) {
  return new Intl.DateTimeFormat("fr-CA", {
    timeZone: "America/Toronto",
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(date);
}

export function parsePreview(stamp) {
  const [iso, hm] = String(stamp).split("T");
  const [hour, minute] = hm.split(":").map(Number);
  return dateInToronto(iso, hour, minute);
}

export const PREVIEW_CLOCKS = [
  ["2026-10-01T20:00", "veille", "Veille 20 h"],
  ["2026-10-02T05:20", "reveil", "02 · 05 h 20"],
  ["2026-10-02T06:15", "route", "02 · 06 h 15"],
  ["2026-10-02T12:30", "sepaq", "02 · 12 h 30"],
  ["2026-10-02T15:15", "chalet", "02 · 15 h 15"],
  ["2026-10-04T05:30", "chasse", "04 · 05 h 30"],
  ["2026-10-04T19:00", "retour", "04 · 19 h"],
  ["2026-10-10T10:00", "bilan", "10 oct."],
];

function at(hour, minute) {
  return dateInToronto("2026-10-02", hour, minute);
}

export const DEPART_EVENTS = [
  { id: "wake", at: at(5, 15), when: "05 h 15", label: "Réveil / dernières affaires", step: "Réveil / dernières affaires", prefix: "Réveil dans" },
  { id: "station", at: at(5, 40), when: "05 h 40", label: "Arrivée prévue à la station-service près de l’aéroport de Gatineau", step: "RDV station-service — 05 h 40", prefix: "RDV station-service dans" },
  { id: "depart", at: at(6, 0), when: "06 h 00", label: "Départ prévu", step: "Départ prévu — 06 h 00", prefix: "Départ dans" },
  { id: "region", at: at(12, 0), when: "Entre 12 h et 13 h", label: "Arrivée prévue dans le secteur de Rivière-à-Pierre / accueil", step: "Arrivée prévue — entre 12 h et 13 h", prefix: "Arrivée prévue dans" },
  { id: "sepaq", at: at(13, 45), when: "13 h 45", label: "Rendez-vous Sépaq — Secteur 4 Desrochers", step: "Sépaq — 13 h 45", prefix: "Sépaq dans" },
  { id: "cabin", at: at(15, 0), when: "Environ 1 h 15 plus tard", label: "Arrivée estimée au chalet", step: "Vers le chalet", prefix: "Chalet estimé dans", note: "Estimation du groupe" },
  { id: "hunt", at: at(16, 30), when: "16 h 30", label: "Début officiel possible de la chasse", step: "Début de chasse — 16 h 30", prefix: "Début de chasse dans" },
];

export const ROAD_LINES = [
  "Dîner",
  "Essence",
  "Communication entre les deux convois",
  "Marcel appelle Sépaq",
];

export const VEILLE_LINES = [
  "Dernières vérifications",
  "Permis",
  "Inscription Sépaq",
  "NordQuest",
  "Matériel chargé",
  "Nourriture",
  "Vêtements",
  "Équipements personnels",
];

export const DESTINATIONS = [
  { id: "sepaq", label: "Accueil Sépaq", query: "229 rue du Lac-Vert, Rivière-à-Pierre, QC G0A 3A0", confirmed: true },
  { id: "gatineau", label: "Station-service près de l’aéroport de Gatineau", query: "station-service aéroport de Gatineau", confirmed: false, note: "Lieu de rendez-vous confirmé. Adresse civique non notée." },
  { id: "chalet", label: "Chalet Desrochers", query: "Chalet Desrochers, Réserve faunique de Portneuf", confirmed: false, note: "Adresse civique du chalet non confirmée. Recherche du nom, pas un point exact." },
  { id: "allard", label: "Boucherie Allard", query: "", confirmed: false, note: "Adresse non confirmée. Pas d’itinéraire tant qu’elle n’est pas notée." },
  { id: "retour", label: "Retour Gatineau", query: "Gatineau, QC", confirmed: false, note: "Retour vers Gatineau. L’adresse de la maison n’est pas notée." },
];

export function mapLinks(query) {
  if (!query) return null;
  const q = encodeURIComponent(query);
  return {
    waze: `https://waze.com/ul?q=${q}&navigate=yes`,
    google: `https://www.google.com/maps/dir/?api=1&destination=${q}&travelmode=driving`,
  };
}

export function remainingLabel(from, to) {
  const ms = to.getTime() - from.getTime();
  if (ms <= 0) return "";
  const total = Math.floor(ms / 60000);
  const days = Math.floor(total / (60 * 24));
  const hours = Math.floor((total - days * 24 * 60) / 60);
  const mins = total % 60;
  const bits = [];
  if (days) bits.push(`${days} jour${days > 1 ? "s" : ""}`);
  if (hours) bits.push(`${hours} h`);
  if (mins && days === 0) bits.push(hours ? String(mins).padStart(2, "0") : `${mins} min`);
  return bits.join(" ");
}

/** Live clock: days, hours, minutes and seconds, including zeros. */
export function countdownClock(from, to) {
  const ms = to.getTime() - from.getTime();
  if (ms <= 0) return null;
  let total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  total -= days * 86400;
  const hours = Math.floor(total / 3600);
  total -= hours * 3600;
  const minutes = Math.floor(total / 60);
  const seconds = total - minutes * 60;
  return { days, hours, minutes, seconds };
}

export function todayTitle(now) {
  const iso = torontoIso(now);
  if (iso < "2026-10-02") return "Aujourd’hui — Préparation";
  if (iso === "2026-10-02") return "Aujourd’hui — Départ";
  if (iso >= "2026-10-03" && iso <= "2026-10-09") return `Aujourd’hui — Jour ${Number(iso.slice(8)) - 2} de chasse`;
  if (iso === "2026-10-10") return "Aujourd’hui — Retour";
  return "Aujourd’hui — Bilan";
}

export function defaultDestination(now) {
  const iso = torontoIso(now);
  const minutes = torontoMinutes(now);
  if (iso > "2026-10-09") return "retour";
  if (iso > "2026-10-02" || (iso === "2026-10-02" && minutes >= 13 * 60 + 45)) return "chalet";
  if (iso === "2026-10-02") return "sepaq";
  return "gatineau";
}

export function nextStep(now, planEmpty = true) {
  const iso = torontoIso(now);
  const minutes = torontoMinutes(now);
  if (iso > "2026-10-09") return { label: "Faire le bilan", countdown: "" };
  if (iso >= "2026-10-03" && iso <= "2026-10-09") {
    if (minutes >= 17 * 60) return { label: "Retour au chalet", countdown: "" };
    return { label: planEmpty ? "Décider le plan du jour" : "Suivre le plan du jour", countdown: "" };
  }
  if (iso === "2026-10-01") {
    const wake = DEPART_EVENTS[0];
    return { label: "Dernières vérifications", until: wake.at, prefix: "Réveil dans", countdown: `Réveil dans ${remainingLabel(now, wake.at)}` };
  }
  if (iso < "2026-10-02") {
    const depart = DEPART_EVENTS[2];
    return { label: "Départ prévu — 2 octobre, 06 h 00", until: depart.at, prefix: "Départ dans", countdown: `Départ dans ${remainingLabel(now, depart.at)}` };
  }
  if (minutes >= 6 * 60 && minutes < 12 * 60) {
    const region = DEPART_EVENTS[3];
    return { label: "Appeler Sépaq", until: region.at, prefix: "Arrivée prévue dans", countdown: `Arrivée prévue dans ${remainingLabel(now, region.at)}` };
  }
  if (minutes >= 16 * 60 + 30) {
    return { label: "Souper Éric — pâtes au pesto + merguez", countdown: "" };
  }
  const upcoming = DEPART_EVENTS.find((event) => event.at.getTime() > now.getTime());
  if (!upcoming) return { label: "Souper Éric — pâtes au pesto + merguez", countdown: "" };
  const left = remainingLabel(now, upcoming.at);
  return { label: upcoming.step, until: upcoming.at, prefix: upcoming.prefix, countdown: left ? `${upcoming.prefix} ${left}` : "" };
}

export function showTimeline(now) {
  const iso = torontoIso(now);
  return iso === "2026-10-01" || iso === "2026-10-02";
}
